import type { Message } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { VoiceChannelData } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/channel-data";
import PhoneIncomingIcon from "@redotech/redo-icons/phone-incoming-01_filled.svg";
import PhoneOutgoingIcon from "@redotech/redo-icons/phone-outgoing-01_filled.svg";
import RecordingIcon from "@redotech/redo-icons/recording-02_filled.svg";
import UsersIcon from "@redotech/redo-icons/users-01_filled.svg";
import { RedoAudioPlayer } from "@redotech/redo-web/arbiter-components/audio/redo-audio-player";
import {
  RedoBadge,
  type RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useRef, useState } from "react";
import {
  useGetCallParticipants,
  type CallParticipantHistory,
} from "../../voice/use-voice-actions";

const STATUS_COLOR: Record<VoiceChannelData["status"], RedoBadgeColor> = {
  ringing: "warning",
  answered: "success",
  completed: "success",
  voicemail: "blue",
  missed: "gray",
  failed: "error",
};

const STATUS_LABEL: Record<VoiceChannelData["status"], string> = {
  ringing: "Ringing",
  answered: "Answered",
  completed: "Completed",
  voicemail: "Voicemail",
  missed: "Missed",
  failed: "Failed",
};

interface VoiceMessageCardProps {
  /**
   * The voice message — its `channel` is narrowed to "voice" by the
   * caller, so `channelData` is `VoiceChannelData | null` here.
   */
  readonly message: Extract<Message, { channel: "voice" }>;
  /** Customer phone number from the thread channel data, used as the heading. */
  readonly customerNumber: string | null;
}

export const VoiceMessageCard = memo(function VoiceMessageCard({
  message,
  customerNumber,
}: VoiceMessageCardProps) {
  const [recordingOpen, setRecordingOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [participants, setParticipants] = useState<
    readonly CallParticipantHistory[] | null
  >(null);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const fetchStartedRef = useRef(false);
  const getCallParticipants = useGetCallParticipants();

  const channelData = message.channelData;
  const direction = channelData?.direction ?? "inbound";
  const status = channelData?.status ?? "completed";
  const recordingUrl = channelData?.recordingUrl ?? null;
  const durationSeconds = channelData?.durationSeconds ?? null;
  const startedAt = channelData?.startedAt ?? message.sentAt;

  const directionLabel =
    direction === "inbound" ? "Incoming call" : "Outgoing call";
  const DirectionIcon =
    direction === "inbound" ? PhoneIncomingIcon : PhoneOutgoingIcon;
  const showParticipantsButton = status === "completed";

  // Lazy-load participants the first time the panel is opened. We
  // gate on a ref instead of `participantsLoading` state so the effect
  // doesn't re-run (and tear down the in-flight promise) when we flip
  // the loading flag.
  useEffect(() => {
    if (!participantsOpen || fetchStartedRef.current) {
      return;
    }
    fetchStartedRef.current = true;
    setParticipantsLoading(true);
    void getCallParticipants({ messageId: message.id })
      .then((rows) => setParticipants(rows))
      .catch((err) => {
        console.error("getCallParticipants failed", err);
        setParticipants([]);
      })
      .finally(() => setParticipantsLoading(false));
  }, [participantsOpen, getCallParticipants, message.id]);

  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      p="xl"
      radius="sm"
    >
      <Flex align="center" gap="md">
        <RedoFeaturedIcon color="brand" Icon={DirectionIcon} />
        <Flex dir="column" gap="none" minW="none" overflow="hidden">
          <Flex align="center" gap="sm">
            <Text fontSize="sm" fontWeight="semibold">
              {directionLabel}
            </Text>
            <RedoBadge
              color={STATUS_COLOR[status]}
              size="xs"
              text={STATUS_LABEL[status]}
            />
          </Flex>
          {customerNumber && (
            <Text fontSize="xs" textColor="secondary">
              {customerNumber}
            </Text>
          )}
          <Text fontSize="xxs" textColor="tertiary">
            {formatStartedAt(startedAt)}
            {durationSeconds != null
              ? ` • ${formatDuration(durationSeconds)}`
              : ""}
          </Text>
        </Flex>
      </Flex>
      {/*
        Fixed slots — Participants always anchors left, Recording always
        anchors right. An empty slot at a glance means that feature
        isn't available on the call.
      */}
      {(showParticipantsButton || recordingUrl) && (
        <Flex align="center" gap="sm" justify="space-between">
          <Flex align="center">
            {showParticipantsButton && (
              <RedoButton
                hierarchy="secondary"
                IconLeading={UsersIcon}
                onClick={() => setParticipantsOpen((o) => !o)}
                size="xs"
                text={participantsOpen ? "Hide participants" : "Participants"}
              />
            )}
          </Flex>
          <Flex align="center">
            {recordingUrl && (
              <RedoButton
                hierarchy="secondary"
                IconLeading={RecordingIcon}
                onClick={() => setRecordingOpen((o) => !o)}
                size="xs"
                text={recordingOpen ? "Hide recording" : "Recording"}
              />
            )}
          </Flex>
        </Flex>
      )}
      {recordingOpen && recordingUrl && (
        <RedoAudioPlayer
          audioSrc={recordingUrl}
          fullWidth
          showCustomActions={false}
        />
      )}
      {participantsOpen && (
        <ParticipantList
          loading={participantsLoading && participants === null}
          participants={participants ?? []}
        />
      )}
    </Flex>
  );
});

const PARTICIPANT_KIND_LABEL: Record<CallParticipantHistory["kind"], string> = {
  customer: "Customer",
  agent: "Agent",
  external: "External",
};

const ParticipantList = memo(function ParticipantList({
  loading,
  participants,
}: {
  readonly loading: boolean;
  readonly participants: readonly CallParticipantHistory[];
}) {
  if (loading) {
    return (
      <Text fontSize="xs" textColor="secondary">
        Loading participants…
      </Text>
    );
  }
  if (participants.length === 0) {
    return (
      <Text fontSize="xs" textColor="secondary">
        No participants on this call.
      </Text>
    );
  }
  return (
    <Flex bgColor="secondary" dir="column" gap="sm" p="md" radius="sm">
      {participants.map((p, idx) => (
        <Flex
          align="center"
          gap="md"
          // Mongo ids alone aren't enough — multiple legs can share a
          // user (re-joins) and externals have no id at all. Compose a
          // deterministic key from kind + leg id (or index fallback).
          justify="space-between"
          key={`${p.kind}-${p.callControlId ?? idx}`}
        >
          <Flex dir="column" gap="none" minW="none" overflow="hidden">
            <Text fontSize="xs" fontWeight="medium">
              {participantPrimaryLabel(p)}
            </Text>
            <Text fontSize="xxs" textColor="tertiary">
              {PARTICIPANT_KIND_LABEL[p.kind]}
              {participantSecondaryLabel(p)
                ? ` • ${participantSecondaryLabel(p)}`
                : ""}
            </Text>
          </Flex>
          <Text fontSize="xxs" textColor="tertiary">
            {formatParticipantTimes(p)}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
});

function participantPrimaryLabel(p: CallParticipantHistory): string {
  if (p.kind === "customer") {
    return p.name || p.phoneNumber || "Customer";
  }
  if (p.kind === "agent") {
    return p.name || "Agent";
  }
  return p.phoneNumber || "External";
}

function participantSecondaryLabel(p: CallParticipantHistory): string {
  if (p.kind === "customer") {
    return p.phoneNumber || "";
  }
  if (p.kind === "external") {
    return p.phoneNumber || "";
  }
  return "";
}

function formatParticipantTimes(p: CallParticipantHistory): string {
  if (p.kind === "customer") {
    return "";
  }
  const join = p.answeredAt ?? p.rangAt;
  const leave = p.leftAt;
  if (join && leave) {
    return `${formatTime(join)} – ${formatTime(leave)}`;
  }
  if (join) {
    return `Joined ${formatTime(join)}`;
  }
  if (leave) {
    return `Left ${formatTime(leave)}`;
  }
  return "";
}

function formatTime(date: Date): string {
  return date.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatStartedAt(date: Date): string {
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const mins = Math.floor(seconds / 60);
  const rem = seconds % 60;
  return `${mins}m ${rem}s`;
}
